<template>
	<ion-modal ref="modal" :is-open="showModal" @did-dismiss="closeGlobalModal">
		<ion-header>
			<ion-toolbar color="branding">
				<ion-buttons slot="end">
					<ion-button color="medium" @click="dismiss">
						<ion-icon :icon="close" />
					</ion-button>
				</ion-buttons>
				<ion-title>{{ $t("user-modal.title") }}</ion-title>
			</ion-toolbar>
		</ion-header>

		<ion-content>
			<GenericLoader v-if="isLoadingUser" />
			<div
				v-else-if="isError"
				class="w-full p-4 text-center text-red-600"
			>
				Error Loading User
			</div>
			<div v-else class="h-full w-full px-2 pb-6 pt-2">
				<UserInfoCard :user="user" class="mb-3" />
				<div class="flex flex-col px-3">
					<template
						v-if="
							myUser?.hasAnyPermission(
								PermissionKey.ManageUserAccounts,
								PermissionKey.AssignPermissions,
							) && !isSelf
						"
					>
						<div>{{ $t("user-modal.admin-actions") }}</div>
						<div class="flex flex-row gap-0.5 overflow-x-auto">
							<!-- prettier-ignore -->
							<template v-if="(user instanceof GVAUserPrivilegedDTO)">
							<template v-if="user?.active">
								<ion-button
									v-if="myUser.hasPermission(PermissionKey.AssignPermissions)"
									size="small"
									color="primary"
									@click="editUserPermissionsModal?.openModal()"
								>
								{{ $t("user-modal.edit-permissions") }}
								</ion-button>
								<ion-button
									v-if="myUser.hasAnyPermission(PermissionKey.ManageUserRoles, PermissionKey.AssignPermissions)"
									size="small"
									color="primary"
									@click="editUserRolesModal?.openModal()"
								>
								{{ $t("user-modal.edit-roles") }}
								</ion-button>
							</template>
							<GetPasswordResetLink
								v-if="!isSelf && user.isManagedAccount && myUser.hasPermission(PermissionKey.ManageUserAccounts)"
								:user="user"
							/>
							<ActivateDeactivateButton
								v-if="!isSelf"
								:user="user"
							/>
						</template>
						</div>
					</template>

					<template
						v-if="
							user instanceof GVAUserPrivilegedDTO &&
							myUser?.internalUser
						"
					>
						<div class="mt-2">Internal Actions</div>
						<div class="flex flex-row gap-0.5 overflow-x-auto">
							<SendPasswordResetButton
								v-if="!user.isManagedAccount"
								:user="user"
							/>
						</div>
					</template>
				</div>
			</div>
		</ion-content>
	</ion-modal>

	<!-- prettier-ignore -->
	<EditUserPermissions
		v-if="(user instanceof GVAUserPrivilegedDTO)"
		ref="editUserPermissionsModal"
		:user="user"
	/>

	<!-- prettier-ignore -->
	<EditUserRolesModal
		v-if="(user instanceof GVAUserPrivilegedDTO)"
		ref="editUserRolesModal"
		:user="user"
	/>
</template>

<script setup lang="ts">
	import {
		IonModal,
		IonHeader,
		IonToolbar,
		IonButton,
		IonButtons,
		IonIcon,
		IonTitle,
		IonContent,
	} from "@ionic/vue";
	import { close } from "ionicons/icons";
	import { useMyUser, useUser } from "seedgreen-shared/queries/user";
	import { computed, ref } from "vue";
	import { useRoute } from "vue-router";
	import GenericLoader from "@/components/ui/GenericLoader.vue";
	import { PermissionKey } from "seedgreen-shared/dtos/PermissionTypeDTO";
	import { GVAUserPrivilegedDTO } from "seedgreen-shared/dtos/GVAUserDTO";
	import UserInfoCard from "@/components/feature/user/UserModal/UserInfoCard.vue";
	import ActivateDeactivateButton from "@/components/feature/user/ActivateUserButton.vue";
	import SendPasswordResetButton from "@/components/feature/user/SendPasswordResetButton.vue";
	import GetPasswordResetLink from "@/components/feature/user/GetPasswordResetLink.vue";
	import EditUserPermissions from "@/components/feature/user/permission/EditUserPermissionsModal.vue";
	import EditUserRolesModal from "@/components/feature/user/role/EditUserRolesModal.vue";
	import { useModalNavigation } from "@/composables/useModalNavigation";

	const modal = ref<InstanceType<typeof IonModal>>();

	const route = useRoute();
	const { myUser } = useMyUser();
	const { closeGlobalModal } = useModalNavigation();

	const editUserPermissionsModal =
		ref<InstanceType<typeof EditUserPermissions>>();
	const editUserRolesModal = ref<InstanceType<typeof EditUserRolesModal>>();

	const showModal = computed(() => route.query.modal === "user-details");

	const isSelf = computed(() => myUser.value?.id === userId.value);

	const userId = computed(() => {
		const query = route.query.userDetailsId;
		return query ? parseInt(query as string) : undefined;
	});

	const { user, isLoading: isLoadingUser, isError } = useUser(userId);

	function dismiss() {
		editUserPermissionsModal.value?.closeModal();
		editUserRolesModal.value?.closeModal();
		modal.value?.$el.dismiss();
	}
</script>
